import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentHeader,
  DocumentSection,
  DocumentInfoGrid,
  DocumentTable,
  DocumentSignatureBlock,
  DocumentFooter,
  DocumentModalShell,
} from './DocumentBranding';
import { downloadElementAsPdf } from '../../../utils/documentPdfDownload';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const fmtMoney = (n) =>
  n === null || n === undefined || n === '' ? '—' : `₦${Number(n).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;

const CRITERIA = [
  ['Unit Price', 'unit_price', fmtMoney],
  ['Total Quoted Price', 'total_price', fmtMoney],
  ['Taxes / Duties', 'taxes_duties'],
  ['Freight / Shipping', 'freight'],
  ['Payment Terms', 'payment_terms'],
  ['Lead Time / Delivery', 'lead_time'],
  ['Warranty', 'warranty'],
  ['Quality Certification', 'quality_cert'],
  ['Past Performance Rating', 'past_performance_rating'],
  ['Compliance with Specs', 'compliance_specs'],
  ['Overall Score (1-10)', 'overall_score'],
];

const CompetitiveAnalysisDocumentView = ({ analysis, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!analysis) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadElementAsPdf('procurement-document-content', `Competitive_Analysis_${analysis.reference_no}.pdf`);
    } catch (error) {
      console.error('Error generating Competitive Analysis PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const vendors = analysis.vendors_compared || [];
  const strengthsWeaknesses = analysis.strengths_weaknesses || [];

  const comparisonRows = CRITERIA.map(([label, key, fmt]) => [
    label,
    ...vendors.map((v) => (fmt ? fmt(v[key]) : v[key] || '—')),
  ]);

  return (
    <DocumentModalShell onDownload={handleDownload} onClose={onClose} downloading={downloading}>
      <DocumentHeader
        title="COMPETITIVE ANALYSIS"
        subtitle={`Ref. ${analysis.reference_no}`}
        badgeLabel="BID COMPARISON"
        badgeColor="bg-indigo-600"
      />

      <div className="bg-blue-50 border-b border-blue-200 px-10 py-4">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Reference No.</p>
            <p className="text-xl font-bold text-blue-900">{analysis.reference_no}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Analysis Date</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(analysis.analysis_date)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Vendors Compared</p>
            <p className="text-lg font-semibold text-gray-900">{vendors.length}</p>
          </div>
        </div>
      </div>

      <DocumentSection title="OVERVIEW">
        <DocumentInfoGrid
          pairs={[
            ['Prepared By', analysis.prepared_by],
            ['Department', analysis.department],
            ['Item / Project Description', analysis.item_project_description],
          ]}
          columns={2}
        />
      </DocumentSection>

      <DocumentSection title="VENDOR COMPARISON">
        <DocumentTable
          headers={['Criteria', ...vendors.map((v) => v.name || 'Vendor')]}
          rows={comparisonRows}
          alignRight={vendors.map((_, i) => i + 1)}
        />
      </DocumentSection>

      <DocumentSection title="STRENGTHS & WEAKNESSES">
        <DocumentTable
          headers={['Vendor', 'Strengths', 'Weaknesses / Risks']}
          rows={strengthsWeaknesses.map((s) => [s.vendor || '—', s.strengths || '—', s.weaknesses_risks || '—'])}
        />
      </DocumentSection>

      <DocumentSection title="RECOMMENDATION">
        <div className="bg-green-50 p-5 rounded-lg border-2 border-green-600">
          <DocumentInfoGrid
            pairs={[
              ['Recommended Vendor', analysis.recommended_vendor],
              ['Estimated Savings / Value', analysis.estimated_savings],
            ]}
          />
        </div>
        <DocumentSignatureBlock labels={['Prepared By', 'Reviewed By', 'Approved By']} />
      </DocumentSection>

      <DocumentFooter documentId={analysis.reference_no} />
    </DocumentModalShell>
  );
};

export default CompetitiveAnalysisDocumentView;
